import { Observable, finalize } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LoadingService } from './loading.service';
import { environment } from '@core/environments/environment';
import { Pagination } from '@core/models/pagination.model';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  private http = inject(HttpClient);
  private loadingService = inject(LoadingService);
  private BASE_URL = environment.ADMIN_BASE_URL + '/v1/report';
  private KYC_BASE_URL = environment.ADMIN_BASE_URL + '/v1/kyc';
  private BANK_BASE_URL = environment.ADMIN_BASE_URL + '/v1/bank';
  private ACCOUNT_BASE_URL = environment.ADMIN_BASE_URL + '/v1/account';

  constructor() {}

  getOrderReport(formData: any, accountID: string): Observable<any> {
    let params = new HttpParams();

    params = params.set('account_id', accountID);

    // Append dates only if they are provided
    if (formData.start_date) {
      params = params.set('start_date', formData.start_date);
    }
    if (formData.end_date) {
      params = params.set('end_date', formData.end_date);
    }
    if (formData.status) {
      params = params.set('status', formData.status);
    }
    return this.http.get(`${this.BASE_URL}/get-order-reports`, { params });
  }

  getNavReport(formData: any, accountID: string): Observable<any> {
    return this.http.get(
      `${this.BASE_URL}/get-nav-reports?type=${formData.type}&account_id=${accountID}`
    );
  }
  getAllEnums(): Observable<any> {
    return this.http.get(`${this.BASE_URL}/get-enums`);
  }
  getAccountStatementReport(
    formData: any,
    account_id: string
  ): Observable<any> {
    const requestBody = {
      ...formData,
      account_id,
    };
    return this.http.post(
      `${this.BANK_BASE_URL}/transaction-statement-generate`,
      requestBody
    );
  }
  getIndividualClientDirectoryReport(accountID: string): Observable<any> {
    return this.http.get(
      `${this.KYC_BASE_URL}/export-individual-kyc-data?account_id=${accountID}`
    );
  }
  exportClientNavReport(accountID: string): Observable<any> {
    return this.http.get(
      `${this.BASE_URL}/export-client-nav-report?account_id=${accountID}`
    );
  }
  getCoperateClientDirectoryReport(accountID: string): Observable<any> {
    return this.http.get(
      `${this.KYC_BASE_URL}/export-corporate-kyc-data?account_id=${accountID}`
    );
  }

  // Global

  getWalletReport(formData: any): Observable<any> {
    let params = new HttpParams();

    // Append dates only if they are provided
    if (formData.start_date) {
      params = params.set('start_date', formData.start_date);
    }
    if (formData.end_date) {
      params = params.set('end_date', formData.end_date);
    }
    return this.http.get(`${this.BASE_URL}/export-transactions`, { params });
  }

  getClientDirectoryReport(formData: any, filterParams?: any): Observable<any> {
    let params = new HttpParams();

    if (formData.start_date && formData.end_date) {
      params = params
        .set('start_date', formData.start_date)
        .set('end_date', formData.end_date);
    }


    if (filterParams) {
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http.get(`${this.BASE_URL}/export-client-directory-bio-data`, {
      params,
    });
  }

  getMangoNairaReport(formData: any): Observable<any> {
    let params = new HttpParams();

    if (formData.end_date) {
      params = params
        .set('end_date', formData.end_date)
        .set('type', formData.type)
        .set('account_id', formData.account_id);
    }

    return this.http.get(`${this.BASE_URL}/generate-mutual-funds-statement`, {
      params,
    });
  }
  getMangoNairaReport2(formData: any): Observable<any> {
    let params = new HttpParams();

    if (formData.end_date) {
      params = params
        .set('end_date', formData.end_date)
        .set('type', formData.type)
        .set('account_id', formData.account_id);
    }

    return this.http.get(
      `${this.BASE_URL}/generate-mutual-funds-statement-v2`,
      {
        params,
      }
    );
  }

  getFundedAccountList(
    data: Pagination = new Pagination(),
    queryString?: string
  ): Observable<any> {
    const uniqueStateUrl = 'get-funded-account-list';
    this.loadingService.setLoading(uniqueStateUrl, true);
    let url = `${this.ACCOUNT_BASE_URL}/get-client-with-fund`;
    let options: { params?: HttpParams } = {};

    if (queryString) {
      url = `${this.ACCOUNT_BASE_URL}/get-client-with-fund?${queryString}`;
    } else {
      const paginationInstance = new Pagination(data);
      // options.params = new HttpParams()
      //   .set('take', paginationInstance.size.toString())
      //   .set('skip', paginationInstance.skip.toString())
    }

    return this.http
      .get<any>(url, options)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
  getActiveAndInactiveAccountList(
    data: Pagination = new Pagination(),
    type: string,
    uniqueUrl: string,
    queryString?: string
  ): Observable<any> {
    const uniqueStateUrl = uniqueUrl;
    this.loadingService.setLoading(uniqueStateUrl, true);
    let url = `${this.BASE_URL}/get-active-and-inactive-clients`;
    let options: { params?: HttpParams } = {};

    if (queryString) {
      url = `${this.BASE_URL}/get-active-and-inactive-clients?${queryString}&report_type=${type}`;
    } else {
      const paginationInstance = new Pagination(data);
      options.params = new HttpParams()
        .set('take', paginationInstance.size.toString())
        .set('skip', paginationInstance.skip.toString())
        .set('report_type', type);
    }

    return this.http
      .get<any>(url, options)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getGlobalPortfolioList(
    data: Pagination = new Pagination(),
    queryString?: string
  ): Observable<any> {
    const uniqueStateUrl = 'get-portfolio-list';
    this.loadingService.setLoading(uniqueStateUrl, true);
    let url = `${this.BASE_URL}/get-global-portfolio-balance`;
    let options: { params?: HttpParams } = {};

    if (queryString) {
      url = `${this.BASE_URL}/get-global-portfolio-balance?${queryString}`;
    } else {
      const paginationInstance = new Pagination(data);
      options.params = new HttpParams()
        .set('take', paginationInstance.size.toString())
        .set('skip', paginationInstance.skip.toString())
    }

    return this.http
      .get<any>(url, options)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getCientWithFundsReport(
    formData: any,
    export_format: string,
    filterParams?: any
  ): Observable<any> {
    let params = new HttpParams();

    params = params.set('export_format', export_format);
    // Append dates only if they are provided
    if (formData.start_date) {
      params = params.set('start_date', formData.start_date);
    }
    if (formData.end_date) {
      params = params.set('end_date', formData.end_date);
    }

    if (filterParams) {
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http.get(`${this.BASE_URL}/get-client-with-fund-report`, {
      params,
    });
  }


  getAccountRMReport(
    RMId?: string,
  ): Observable<any> {
    let params = new HttpParams();

    if (RMId) {
      params = params.set('relationship_manager_id', RMId);
    }

    return this.http.get(`${this.BASE_URL}/export-account-with-rm-info`, {
      params,
    });
  }
  getGlobalPortfolioReport(filterParams?: any): Observable<any> {
    let params = new HttpParams()


    if (filterParams) {
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }
    return this.http.get(
      `${this.BASE_URL}/export-global-portfolio-balance-file`, { params }
    );
  }
  getMFDTransactionReport(data: MFDTransactions): Observable<ExportMFDTransactionResponse> {
    let params = new HttpParams()
      .set('start_date', data.start_date)
      .set('end_date', data.end_date);

      console.log("data", data.account_id)
      if (data.account_id) {
        params = params.set('account_id', data.account_id)
      }
    
    return this.http.get<ExportMFDTransactionResponse>(
      `${this.BASE_URL}/export-mutual-fund-transaction`,
      { params }
    );
  }
  getGeneralDividendReport(data: MFDDividend): Observable<ExportAccountKycResponse> {
    let params = new HttpParams()
      .set('start_date', data.start_date)
      .set('end_date', data.end_date);
    
    return this.http.get<ExportAccountKycResponse>(
      `${this.BASE_URL}/export-dividend-value-report`,
      { params }
    );
  }
  getIndividualDividendReport(data: MFDTransactions): Observable<ExportAccountKycResponse> {
    let params = new HttpParams()
      .set('start_date', data.start_date)
      .set('end_date', data.end_date)
      .set('account_id', data.account_id || '');
    

    
    return this.http.get<ExportAccountKycResponse>(
      `${this.BASE_URL}/export-individual-dividend-report`,
      { params }
    );
  }

  getActiveAndInactiveClientCSVReport(report_type: string, filterParams: any): Observable<any> {
    let params = new HttpParams().set('report_type', report_type);

    if (filterParams) {
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }

    return this.http.get(
      `${this.BASE_URL}/export_active_inactive_or_clients_report`, { params }
    );
  }
  getActiveAndInactiveClientPDFReport(report_type: string, filterParams?: any): Observable<any> {
    let params = new HttpParams().set('report_type', report_type);

    if (filterParams) {
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value));
        }
      });
    }
    return this.http.get(
      `${this.BASE_URL}/export_active_inactive_or_clients_report`, { params }
    );
  }

  exportAccountKyc(accountId: string): Observable<ExportAccountKycResponse> {
    return this.http.get<ExportAccountKycResponse>(
      `${this.BASE_URL}/export-kyc-report-pdf?account_id=${accountId}`
    );
  }

  exportDividendReport(start_date?: string, end_date?: string): Observable<ExportDividendReportResponse> {
    let params = new HttpParams();

    
    if (start_date) {
      params = params.set('start_date', start_date);
    }
    if (end_date) {
      params = params.set('end_date', end_date);
    }

    return this.http.get<ExportDividendReportResponse>(`${this.BASE_URL}/export-dividend-report`, { params });
  }

  getConsolidatedReport(formData: {start_date: string, end_date: string}): Observable<any> {
    let params = new HttpParams();

    if (formData.start_date && formData.end_date) {
      params = params
        .set('start_date', formData.start_date)
        .set('end_date', formData.end_date);
    }

    return this.http.get(`${this.BASE_URL}/export-mfd-consolidated-list`, {
      params,
    });
  }

  getMNMMFStatements(data: Pagination = new Pagination(), queryString?: string): Observable<any> {
    const uniqueStateUrl = 'get-mnmmf-statements';
    this.loadingService.setLoading(uniqueStateUrl, true);
    let url = `${this.BASE_URL}/get-mfd-report-records`;
    let options: { params?: HttpParams } = {};

    if (queryString) {
      url = `${this.BASE_URL}/get-mfd-report-records?${queryString}`;
    } else {
      const paginationInstance = new Pagination(data);
      options.params = new HttpParams()
        .set('take', paginationInstance.size.toString())
        .set('skip', paginationInstance.skip.toString())
    }

    return this.http
      .get<any>(url, options)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  resendMFDStatement(report_record_id: string): Observable<any> {
    const uniqueStateUrl = 'resend-mfd-statement';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http.post(
      `${this.BASE_URL}/resend-single-mfd-report-records`, { report_record_id: report_record_id }
    ).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }

  resendBulkMFDStatement(report_record_ids: string[]): Observable<any> {
    const uniqueStateUrl = 'resend-bulk-mfd-statement';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http.post(
      `${this.BASE_URL}/resend-bulk-mfd-report-records`, { report_record_ids: report_record_ids }
    ).pipe(
      finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
    );
  }
}


interface ExportDividendReportResponse {
  status: string;
  message: string;
  data: {
    statement?: string;
    presigned_url?: string;
  };
}


interface ExportAccountKycResponse {
  status: string;
  message: string;
  data: Data;
}

interface Data {
  presigned_url: string;
}

export interface MFDTransactions{
  start_date: string;
  end_date: string;
  account_id?: string
}
interface MFDDividend{
  start_date: string;
  end_date: string;
}

interface ExportMFDTransactionResponse {
  status: string;
  message: string;
  data: {
    statement: string;
  };
}